import { Suspense, useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import appRoutes from "@zenith/routes/routes";
import { LoadingSpinnerWithBackdrop } from "@zenith/features";
import { GlobalContextProvider } from "@zenith/context";
import { onMessageListener, FcmMessagePayload } from "../firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { captureException, setUser } from "@zenith/utils/sentry";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";

import {
  InternetConnectionComponent,
  ServiceFailureComponent,
  UnstableConnectionComponent,
} from "@zenith/features/AppStateHandlers/components";
import ChunkErrorBoundary from "@zenith/routes/chunk-error-boundary";

declare const window: Window & typeof globalThis;

const ALL_RELOAD_FLAGS = [
  "autoReloadAttempted",
  "reloadAttempted",
  "chunkReloadAttempted",
];

/**
 * Ties every Sentry event to the account it happened to, which is what makes
 * "who was affected" answerable. A failed sign-in carries no user, and clearing
 * the session empties this again.
 */
function SentryUserSync() {
  const { userDetailsContext } = useUserDetailsContext();
  const { id, email } = userDetailsContext;

  useEffect(() => {
    setUser(id ? { id, email: email || undefined } : null);
  }, [id, email]);

  return null;
}

function App() {
  useEffect(() => {
    if (import.meta.env.MODE === "development") {
      return;
    }

    // Store the current version on first load
    const initializeVersion = () => {
      const currentVersion = import.meta.env.VITE_APP_VERSION;
      if (!sessionStorage.getItem("appVersion")) {
        sessionStorage.setItem("appVersion", currentVersion || "1.0.0");
      }
    };
    initializeVersion();

    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) {
          console.warn("Version check failed - response not OK");
          return;
        }

        const data = await response.json();
        const currentVersion = import.meta.env.VITE_APP_VERSION;
        const storedVersion = sessionStorage.getItem("appVersion");

        if (
          data.version &&
          data.version !== currentVersion &&
          data.version !== storedVersion
        ) {
          console.warn(
            `New version detected: ${data.version} (current: ${currentVersion})`
          );

          // Clear all recovery flags so handlers can fire on the new version
          ALL_RELOAD_FLAGS.forEach((f) => sessionStorage.removeItem(f));

          if ("caches" in window) {
            try {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map((name) => caches.delete(name)));
            } catch (cacheError) {
              captureException(cacheError);
            }
          }

          sessionStorage.setItem("appVersion", data.version);
          globalThis.location.reload();
          return;
        }

        console.log("Version check passed - no update needed");
      } catch (error) {
        console.error("Version check failed:", error);
        captureException(error);
      }
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, 2 * 60 * 1000);

    // Intercept /assets/ chunk requests — if Azure SWA serves index.html for a
    // missing chunk (navigationFallback firing despite the exclude rule), detect
    // it early via content-type and force a reload before React errors out.
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = args[0]?.toString() || "";
      if (
        url.includes("/assets/") &&
        (url.endsWith(".js") || url.endsWith(".css"))
      ) {
        try {
          const response = await originalFetch(...args);
          const contentType = response.headers.get("content-type");
          if (contentType?.includes("text/html")) {
            console.warn(`Chunk returned HTML (stale deployment): ${url}`);
            ALL_RELOAD_FLAGS.forEach((f) => sessionStorage.removeItem(f));
            if ("caches" in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map((name) => caches.delete(name)));
            }
            globalThis.location.reload();
            return Promise.reject(new Error("Version mismatch - reloading"));
          }
          return response;
        } catch (error) {
          console.error(`Failed to fetch chunk: ${url}`, error);
          throw error;
        }
      }
      return originalFetch(...args);
    };

    let unsubscribeMessages: (() => void) | undefined;
    try {
      unsubscribeMessages = onMessageListener((payload: FcmMessagePayload) => {
        toast.info(
          <div>
            <h3>{payload.notification?.title}</h3>
            <p>{payload.notification?.body}</p>
          </div>
        );
      });
    } catch (error) {
      console.error("Error listening for messages:", error);
      captureException(error);
    }

    return () => {
      clearInterval(intervalId);
      window.fetch = originalFetch;
      unsubscribeMessages?.();
    };
  }, []);

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<LoadingSpinnerWithBackdrop isloading />}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <GlobalContextProvider>
            <SentryUserSync />
            <LoadingSpinnerWithBackdrop />
            <InternetConnectionComponent />
            <UnstableConnectionComponent />
            {/* <ServiceFailureComponent /> */}
            <RouterProvider router={appRoutes} />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </GlobalContextProvider>
        </LocalizationProvider>
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export default App;
